import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { DEFAULT_FILTERS, URL_PARAM_KEYS } from '@calendar/lib/constants';
import type { FilterState } from '@calendar/components/calendar-filters';

// Extend Window interface to include our preset filters
declare global {
  interface Window {
    curtisCalendarPresetFilters?: Partial<FilterState>;
  }
}

// Helper to parse boolean strings
const parseBoolean = (value: string | null): boolean => value === 'true';

// Parse URL params into a partial FilterState
const parseFiltersFromURL = (): Partial<FilterState> => {
  const params = new URLSearchParams(window.location.search);
  const result: Partial<FilterState> = {};

  if (params.has(URL_PARAM_KEYS.season)) {
    result.season = params.get(URL_PARAM_KEYS.season) as string;
  }
  if (params.has(URL_PARAM_KEYS.month)) {
    const m = params.get(URL_PARAM_KEYS.month);
    result.month = m === null ? null : m;
  }
  if (params.has(URL_PARAM_KEYS.category)) {
    const c = params.get(URL_PARAM_KEYS.category);
    result.category = c === 'all' ? undefined : c ?? undefined;
  }
  if (params.has(URL_PARAM_KEYS.upcomingOnly)) {
    result.upcomingOnly = parseBoolean(params.get(URL_PARAM_KEYS.upcomingOnly));
  }
  if (params.has(URL_PARAM_KEYS.freeOnly)) {
    result.freeOnly = parseBoolean(params.get(URL_PARAM_KEYS.freeOnly));
  }
  if (params.has(URL_PARAM_KEYS.ticketedOnly)) {
    result.ticketedOnly = parseBoolean(params.get(URL_PARAM_KEYS.ticketedOnly));
  }
  if (params.has(URL_PARAM_KEYS.sortBy)) {
    result.sortBy = params.get(URL_PARAM_KEYS.sortBy) as string;
  }
  if (params.has(URL_PARAM_KEYS.view)) {
    result.view = params.get(URL_PARAM_KEYS.view) as string;
  }

  return result;
};

// Serialize current filters to the URL, only storing non-default values
const serializeFiltersToURL = (filters: FilterState) => {
  const params = new URLSearchParams();
  const presetFilters = window.curtisCalendarPresetFilters || {};

  (Object.keys(DEFAULT_FILTERS) as (keyof FilterState)[]).forEach((key) => {
    const defaultValue = DEFAULT_FILTERS[key];
    const currentValue = filters[key];
    const presetValue = presetFilters[key];

    // For category filter, always include it in URL if it has a value
    // This prevents losing preset categories when other filters change
    if (key === 'category' && currentValue !== undefined && currentValue !== null) {
      params.set(URL_PARAM_KEYS[key], String(currentValue));
      return;
    }

    // Skip if this is a preset filter value (don't include preset filters in URL)
    // EXCEPT when it's being explicitly changed by user interaction
    if (presetValue !== undefined && currentValue === presetValue && key !== 'upcomingOnly') {
      return;
    }

    // Only include non-default values in URL
    if (currentValue !== defaultValue) {
      let valueStr: string;
      if (typeof currentValue === 'boolean') {
        valueStr = currentValue.toString();
      } else if (currentValue === null || currentValue === undefined) {
        return;
      } else {
        valueStr = currentValue;
      }
      const paramKey = URL_PARAM_KEYS[key];
      params.set(paramKey, valueStr);
    }
  });

  const newSearch = params.toString();
  const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
  window.history.replaceState(null, '', newUrl);
};

// Hook: sync filter state with URL, localStorage, and history navigation
export function useCalendarFiltersSync(): [FilterState, Dispatch<SetStateAction<FilterState>>] {
  const [ filters, setFilters ] = useState<FilterState>(() => {
    const parsed = parseFiltersFromURL();
    const initial: FilterState = { ...DEFAULT_FILTERS };

    // Apply preset filters first
    if (window.curtisCalendarPresetFilters) {
      const presetFilters = window.curtisCalendarPresetFilters;
      Object.assign(initial, presetFilters);
    }

    // Then override with URL parameters (only if explicitly set in URL)
    Object.assign(initial, parsed);

    // If no view param is present, apply localStorage fallback
    const params = new URLSearchParams(window.location.search);
    if (!params.has(URL_PARAM_KEYS.view)) {
      const stored = localStorage.getItem('calendarView');
      if (stored) {
        initial.view = stored;
      }
    }

    // Normalize: upcomingOnly overrides month, explicit month disables upcomingOnly
    if (initial.upcomingOnly) {
      initial.month = null;
    }
    if (parsed.month) {
      initial.upcomingOnly = false;
    }

    return initial;
  });

  // On filters change: update URL and localStorage
  useEffect(() => {
    serializeFiltersToURL(filters);
    localStorage.setItem('calendarView', filters.view);
  }, [ filters ]);

  // Handle browser back/forward
  useEffect(() => {
    const onPopState = () => {
      const parsed = parseFiltersFromURL();
      const updated: FilterState = { ...DEFAULT_FILTERS, ...parsed };

      const params = new URLSearchParams(window.location.search);
      if (!params.has(URL_PARAM_KEYS.view)) {
        const stored = localStorage.getItem('calendarView');
        if (stored) {
          updated.view = stored;
        }
      }

      if (updated.upcomingOnly) {
        updated.month = null;
      }
      if (parsed.month) {
        updated.upcomingOnly = false;
      }

      setFilters(updated);
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [ setFilters ]);

  return [ filters, setFilters ];
}
